import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Router} from '@angular/router';
import {AbstractControl, AsyncValidatorFn, UntypedFormBuilder, Validators} from '@angular/forms';
import {BusinessNature, BusinessType, PublicService, Risk} from 'web-angular-client';
import {of, Subject, Subscription} from 'rxjs';

import {AlertService} from '../../service/alert.service';
import {AuthenticationService} from '../../service/authentication.service';
import {EnvironmentService} from '../../service/environment.service';
import {SyncValidators} from '../../validator/SyncValidators';
import {AsyncValidators} from '../../validator/AsyncValidators';
import {KycDetail} from '../../interface/model/kyc-detail';
import {WizardStepForm} from '../../interface/form/wizard-step-form';

/**
 * The business-details/KYC form shared by the signup wizard and the eCommerce KYC verification
 * wizard. The nature of business is either captured in-form ({@link captureNatureOfBusiness}) or
 * supplied via {@link natureOfBusiness}; it drives the FSP-number requirement and the emitted
 * KycDetail.
 */
@Component({
    selector: 'app-kyc-form',
    templateUrl: './kyc-form.component.html',
    standalone: false
})
export class KycFormComponent extends WizardStepForm<KycDetail> implements OnInit, OnDestroy, OnChanges
{
    private static readonly PTY_TOOLTIP = 'Expected format: YYYY/XXXXXX/07 (PTY/LTD)';
    private static readonly INC_TOOLTIP = 'Expected format: YYYY/XXXXXX/21 (Inc)';
    private static readonly CC_TOOLTIP = 'Expected format: CK YYYY/XXXXXX/23 (CC) or YYYY/XXXXXX/23';
    private static readonly ANY_TOOLTIP = 'Expected format: YYYY/XXXXXX/07 (PTY/LTD), YYYY/XXXXXX/21 (Inc), CK YYYY/XXXXXX/23 (CC) or YYYY/XXXXXX/23';
    private static readonly INSURANCE_NATURE_OF_BUSINESS = 'Insurance';
    private static readonly OTHER_NATURE_OF_BUSINESS = 'Other';

    /**
     * When enabled, the nature of business is captured in-form with a dropdown and an "Other"
     * free-text option. When disabled, the {@link natureOfBusiness} input supplies it instead.
     */
    @Input()
    captureNatureOfBusiness = false;

    /**
     * The already-captured nature of business; only used outside capture mode, where it drives the
     * FSP-number requirement and is carried into the emitted KycDetail.
     */
    @Input()
    natureOfBusiness: string;

    @Input()
    businessTypeSubject: Subject<BusinessType>;

    @Input()
    prefilledKycDetail: KycDetail;

    @Input()
    directorNameSubject: Subject<string>;

    @Input()
    directorSurnameSubject: Subject<string>;

    /** Forwarded to the duplicate-registration check so integrator-managed merchants validate within their prefix. */
    @Input()
    integratorPrefix: string;

    /**
     * Skips the duplicate-registration check while the value matches the prefilled registration
     * number, so a merchant's own stored number does not read as a duplicate of itself.
     */
    @Input()
    skipDuplicateCheckForPrefilled = false;

    @Output()
    previousPage: EventEmitter<boolean>;

    @Output()
    kycDetail: EventEmitter<KycDetail>;

    @Output()
    nextPage: EventEmitter<number>;

    natureOfBusinessList: BusinessNature[];
    otherNatureOfBusinessSelected: boolean;

    private businessType: BusinessType;
    private businessTypeSubscription: Subscription;
    private directorNameSubscription: Subscription;
    private directorSurnameSubscription: Subscription;
    private natureOfBusinessSubscription: Subscription;

    constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected publicService: PublicService,
        protected environmentService: EnvironmentService)
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.previousPage = new EventEmitter();
        this.kycDetail = new EventEmitter();
        this.nextPage = new EventEmitter<number>();
    }

    get formControls()
    {
        return this.form.controls;
    }

    get isJuristicMerchant()
    {
        return this.businessType !== BusinessType.SOLE_PROPRIETOR;
    }

    getRegistrationNumberTooltip(): string
    {
        switch (this.businessType)
        {
            case BusinessType.PTY:
                return KycFormComponent.PTY_TOOLTIP;
            case BusinessType.INCORPORATED_COMPANY_INC:
                return KycFormComponent.INC_TOOLTIP;
            case BusinessType.CLOSED_CORPORATION:
                return KycFormComponent.CC_TOOLTIP;
            default:
                return KycFormComponent.ANY_TOOLTIP;
        }
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.patchPrefilledDataToForm(this.prefilledKycDetail);

        if (changes['natureOfBusiness'] && this.form && !this.captureNatureOfBusiness)
        {
            this.applyFspNumberRequirement(this.natureOfBusiness);
        }
    }

    ngOnInit(): void
    {
        this.form = this.formBuilder.group({
            registrationNumber: [
                '', [Validators.required, SyncValidators.validateBusinessRegistrationNumber(() => this.businessType)]
            ],
            directorName: ['', Validators.required],
            directorSurname: ['', Validators.required],
            directorIdNumber: ['', [Validators.required, SyncValidators.validateSAIdNumber]],
            fspNumber: ['', SyncValidators.validateFSPNumber()],
            websiteUrl: ['', Validators.maxLength(100)],
            overview: ['', [Validators.required, Validators.maxLength(250)]]
        });

        if (this.captureNatureOfBusiness)
        {
            this.setUpNatureOfBusinessCapture();
        }
        else
        {
            this.applyFspNumberRequirement(this.natureOfBusiness);
        }

        this.loadRegistrationNumberFormComponent();
        this.loadDirectorName();
        this.loadDirectorSurname();
        this.patchPrefilledDataToForm(this.prefilledKycDetail);
        this.applyReadOnlyState();
    }

    ngOnDestroy(): void
    {
        this.businessTypeSubscription?.unsubscribe();
        this.directorNameSubscription?.unsubscribe();
        this.directorSurnameSubscription?.unsubscribe();
        this.natureOfBusinessSubscription?.unsubscribe();
    }

    /**
     * Emits the captured KYC details — carrying the nature of business from the dropdown or the
     * input, depending on the mode — and moves the wizard to the next step. In read-only "View
     * submission" mode the details are not emitted, so the disabled form's values cannot overwrite
     * the hosting wizard's state.
     */
    submit(): void
    {
        if (!this.readOnly)
        {
            this.kycDetail.emit(this.getKycDetailFromForm());
        }
        this.nextPage.emit();
    }

    /**
     * Lets the hosting wizard know the user wants to go back to the previous step.
     */
    emitGoToPrevious(): void
    {
        this.previousPage.emit(true);
    }

    /**
     * Builds the output model from the form values.
     */
    protected getKycDetailFromForm(): KycDetail
    {
        return {
            directorName: this.formControls.directorName.value,
            directorSurname: this.formControls.directorSurname.value,
            directorIdNumber: this.formControls.directorIdNumber.value,
            registrationNumber: this.formControls.registrationNumber.value,
            fspNumber: this.formControls.fspNumber.value,
            websiteUrl: this.formControls.websiteUrl.value,
            overview: this.formControls.overview.value,
            natureOfBusiness: this.getNatureOfBusiness(),
            token: null
        };
    }

    /**
     * Resolves the nature of business for the emitted model: the dropdown selection (or its
     * "Other" free text) in capture mode, the supplied input otherwise.
     */
    private getNatureOfBusiness(): string
    {
        if (!this.captureNatureOfBusiness)
        {
            return this.natureOfBusiness;
        }
        return this.otherNatureOfBusinessSelected ?
            this.formControls.natureOfBusinessOther.value : this.formControls.natureOfBusiness.value;
    }

    /**
     * Adds the in-form nature-of-business controls, loads the selectable list and wires the
     * dropdown to toggle the "Other" free-text control and the Insurance-driven FSP requirement.
     */
    private setUpNatureOfBusinessCapture(): void
    {
        this.form.addControl('natureOfBusiness', this.formBuilder.control('', Validators.required));
        this.form.addControl('natureOfBusinessOther',
            this.formBuilder.control('', [Validators.required, Validators.maxLength(400)]));

        this.loadNatureOfBusinessList();

        this.natureOfBusinessSubscription = this.formControls.natureOfBusiness.valueChanges.subscribe(natureOfBusiness =>
        {
            if (natureOfBusiness === KycFormComponent.OTHER_NATURE_OF_BUSINESS)
            {
                this.otherNatureOfBusinessSelected = true;
                this.formControls.natureOfBusinessOther.enable();
            }
            else
            {
                this.otherNatureOfBusinessSelected = false;
                this.formControls.natureOfBusinessOther.disable();
            }

            this.applyFspNumberRequirement(natureOfBusiness);
        });
    }

    /**
     * Loads the selectable nature-of-business list with the manual "Other" option appended, then
     * applies the prefilled selection.
     */
    private loadNatureOfBusinessList(): void
    {
        this.publicService.getNatureOfBusinessList().subscribe({
            next: (natureOfBusinessList: BusinessNature[]) =>
            {
                natureOfBusinessList.push({name: KycFormComponent.OTHER_NATURE_OF_BUSINESS, risk: Risk.LOW});
                this.natureOfBusinessList = natureOfBusinessList;
                this.formControls.natureOfBusiness.patchValue(this.prefilledKycDetail?.natureOfBusiness ?? null);
            },
            error: () =>
            {
                this.alertService.error('An error occurred while attempting to load nature of business list. Please try' +
                    ' again later.', true);
            }
        });
    }

    /**
     * Makes the FSP number required when the nature of business is Insurance, and optional
     * otherwise.
     */
    private applyFspNumberRequirement(natureOfBusiness: string): void
    {
        if (natureOfBusiness === KycFormComponent.INSURANCE_NATURE_OF_BUSINESS)
        {
            this.formControls.fspNumber.addValidators([Validators.required]);
        }
        else
        {
            this.formControls.fspNumber.removeValidators([Validators.required]);
        }
        this.formControls.fspNumber.updateValueAndValidity();
    }

    /**
     * Enables the registration number control — with its duplicate-registration async validator —
     * for juristic businesses, and disables it for sole proprietors. Driven by the business type
     * chosen on the previous step.
     */
    private loadRegistrationNumberFormComponent(): void
    {
        if (!this.businessTypeSubject)
        {
            return;
        }
        this.businessTypeSubscription = this.businessTypeSubject.subscribe(businessType =>
        {
            if (!businessType || this.readOnly)
            {
                return;
            }
            this.businessType = businessType;

            if (this.isJuristicMerchant)
            {
                this.formControls.registrationNumber.enable();
                this.formControls.registrationNumber.clearAsyncValidators();
                this.formControls.registrationNumber.addAsyncValidators(this.registrationNumberDuplicateValidator());
                this.formControls.registrationNumber.updateValueAndValidity();
            }
            else
            {
                this.formControls.registrationNumber.reset(null, {emitEvent: false});
                this.formControls.registrationNumber.clearAsyncValidators();
                this.formControls.registrationNumber.disable({emitEvent: false});
            }
        });
    }

    /**
     * Pre-populates the directorName form component with a name provided from an input subject if
     * available.
     */
    private loadDirectorName(): void
    {
        if (this.directorNameSubject)
        {
            this.directorNameSubscription = this.directorNameSubject.subscribe(directorName =>
            {
                this.formControls.directorName.setValue(directorName);
            });
        }
    }

    /**
     * Pre-populates the directorSurname form component with a surname provided from an input
     * subject if available.
     */
    private loadDirectorSurname(): void
    {
        if (this.directorSurnameSubject)
        {
            this.directorSurnameSubscription = this.directorSurnameSubject.subscribe(directorSurname =>
            {
                this.formControls.directorSurname.setValue(directorSurname);
            });
        }
    }

    /**
     * The duplicate-registration-number async validator, optionally skipped when the value is the
     * merchant's own already-stored registration number so it does not read as a duplicate of
     * itself.
     */
    private registrationNumberDuplicateValidator(): AsyncValidatorFn
    {
        const duplicateValidator: AsyncValidatorFn = AsyncValidators.validateBusinessRegistrationNumber(
            this.publicService, this.businessType, this.integratorPrefix ?? null);

        return (control: AbstractControl) =>
        {
            if (this.skipDuplicateCheckForPrefilled && this.isOwnRegistrationNumber(control.value))
            {
                return of(null);
            }
            return duplicateValidator(control);
        };
    }

    /**
     * Whether the given value matches the merchant's prefilled registration number, compared with
     * whitespace removed the same way the duplicate check normalises it.
     */
    private isOwnRegistrationNumber(value: string): boolean
    {
        const prefilledRegistrationNumber: string = this.prefilledKycDetail?.registrationNumber;
        if (!prefilledRegistrationNumber || !value)
        {
            return false;
        }
        return value.replace(/\s+/g, '') === prefilledRegistrationNumber.replace(/\s+/g, '');
    }
}
